import { defineChatActions } from './actionsContext'
import { clearMessages, dropRoomMessages } from './messageRoomCache'
import { checkAuth } from './runtime'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type {
  IChat,
} from '@/modules/chat-matrix-fast/types/IChat'
import {
  isEventChatItem,
  mergeBackendChatWithMatrix,
  pickTotalMembers,
  preserveEventChatMetadata,
  preserveTotalMembers,
} from '@/modules/chat-matrix-fast/constants/chat'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { pollChatUntilMatrixRoom } from '@/modules/chat-matrix-fast/services/matrixRoomPolling'
import {
  sameChatId,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  useCurrentUser,
} from '@/modules/chat-matrix-fast/composables/useCurrentUser'
import {
  deleteMatrixChat,
  fetchChatFromMatrix,
  isSyntheticMatrixChatId,
  leaveAndForgetMatrixRoom,
  roomIdToChatId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { isFallbackDialogTitle } from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import {
  phoneToMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixUserId'
import { isForgottenRoom } from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import { clearStoredSelectedChatId } from '@/modules/chat-matrix-fast/utils/chatListPersistence'
import {
  getCachedMatrixLastSeenAt,
  getCachedMatrixPresence,
  trackMatrixPresence,
} from '@/modules/chat-matrix-fast/services/matrixPresenceService'
import { resolveDialogPeerMxid } from '@/modules/chat-matrix-fast/utils/dialogPresence'

import {
  notifyViewRemoveChat,
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import {
  clearMatrixSubscription,
} from '@/modules/chat-matrix-fast/stores/matrixSubscriptions'

/** Дедуп параллельных refreshChatMemberCount(chatId) из openChat. */
const memberCountRefreshInFlight = new Map<string, Promise<void>>()

export const dialogActions = defineChatActions({
  /** Matrix-only диалог → POST /dialog/, backend id + dialog_member + аватар */
  async registerMatrixOnlyDialog(chat: IChat): Promise<IChat | null> {
    if (chat.type !== 'dialog' && chat.type !== 'direct') {
      return null
    }

    const peerMxid = chat.matrix_peer_id?.trim()
    if (!peerMxid) {
      return null
    }

    const peerEmployeeId = await this.resolveEmployeeIdByMatrixUserId(peerMxid)
    if (!peerEmployeeId) {
      return null
    }

    try {
      let backendChat = await ChatApiService.createOrGetDialog(peerEmployeeId)
      const matrixRoomId = chat.matrix_room_id?.trim() || backendChat.matrix_room_id

      if (matrixRoomId) {
        try {
          const matrixChat = await fetchChatFromMatrix(chat.id, chat)
          backendChat = mergeBackendChatWithMatrix(backendChat, {
            ...matrixChat,
            matrix_room_id: matrixRoomId,
            matrix_peer_id: peerMxid,
          })
        } catch {
          backendChat = {
            ...backendChat,
            matrix_room_id: matrixRoomId,
            matrix_peer_id: peerMxid,
            last_message: chat.last_message ?? backendChat.last_message,
            last_message_id: chat.last_message_id ?? backendChat.last_message_id,
            unread_count: chat.unread_count ?? backendChat.unread_count,
          }
        }
      }

      return this.resolveFallbackDialogTitle(backendChat)
    } catch {
      return null
    }
  },

  async findExistingDirectChat(userId: string): Promise<IChat | undefined> {
    const employeeInfo = await this.resolveEmployeeInfo(userId)
    const peerMxid = employeeInfo.matrixUserId
        || (employeeInfo.phone ? phoneToMatrixUserId(employeeInfo.phone) : undefined)
    const currentUser = useCurrentUser()
    const currentUserId = currentUser.id.value

    return this.chats.find(chat => {
      if (chat.type !== 'direct' && chat.type !== 'dialog') {
        return false
      }

      if (peerMxid && chat.matrix_peer_id === peerMxid) {
        return true
      }

      if (chat.dialog_member?.id && String(chat.dialog_member.id) === String(userId)) {
        return true
      }

      if (
        !currentUserId
          || !chat.members
          || !Array.isArray(chat.members)
          || chat.members.length !== 2
      ) {
        return false
      }

      const memberIds = chat.members.map(member => String(member.user_uuid || member.user || ''))

      return memberIds.includes(String(currentUserId)) && memberIds.includes(String(userId))
    })
  },

  upsertChatInList(newChat: IChat): void {
    newChat = this.resolveFallbackDialogTitle(newChat)

    const existingChatIndex = this.chats.findIndex(chat =>
      chat.id === newChat.id
        || (chat.matrix_room_id && chat.matrix_room_id === newChat.matrix_room_id)
        || (chat.matrix_peer_id && newChat.matrix_peer_id && chat.matrix_peer_id === newChat.matrix_peer_id),
    )

    if (existingChatIndex !== -1) {
      const previous = this.chats[existingChatIndex]
      const merged = isEventChatItem(previous) || isEventChatItem(newChat)
        ? preserveEventChatMetadata(previous, newChat)
        : { ...previous, ...newChat }
      this.chats[existingChatIndex] = {
        ...merged,
        total_members: preserveTotalMembers(newChat.total_members, previous.total_members),
      }
    } else {
      this.chats.unshift(newChat)
    }

    syncViewWithCore()
  },

  /** Не затираем total_members из /members или списка устаревшим объектом чата. */
  setCurrentChatPreservingMemberCount(chat: IChat): void {
    const listed = this.chats.find(c => sameChatId(c.id, chat.id))
    const fromList = listed?.total_members
    const fromCurrent = this.currentChat && sameChatId(this.currentChat.id, chat.id)
      ? this.currentChat.total_members
      : undefined
    const isBackendChat = typeof chat.id === 'number'
      && !isSyntheticMatrixChatId(chat.id, { matrix_room_id: chat.matrix_room_id })

    let fromMatrix: number | undefined
    if (!isBackendChat) {
      const roomId = chat.matrix_room_id?.trim() || listed?.matrix_room_id?.trim()
      if (roomId) {
        try {
          const count = getMatrixClient().getRoom(roomId)?.getJoinedMemberCount()
          if (typeof count === 'number' && count > 0) {
            fromMatrix = count
          }
        } catch {
          // client ещё не поднят
        }
      }
    }

    // Backend total_members — источник истины для групп; Matrix только для matrix-only диалогов.
    const totalMembers = isBackendChat
      ? pickTotalMembers(chat.total_members, pickTotalMembers(fromList, fromCurrent))
      : pickTotalMembers(
        preserveTotalMembers(fromList, fromCurrent, fromMatrix),
        chat.total_members,
      )

    this.currentChat = {
      ...chat,
      total_members: totalMembers,
    }
  },

  /** `count` из GET /chat/{id}/members/ — для шапки; GET /chat/{id}/ часто отдаёт неполный members. */
  async refreshChatMemberCount(chatId: number | string): Promise<void> {
    if (typeof chatId === 'number' && isSyntheticMatrixChatId(chatId)) {
      return
    }

    const chatSnapshot = this.chats.find(chat => sameChatId(chat.id, chatId))
      ?? (this.currentChat && sameChatId(this.currentChat.id, chatId) ? this.currentChat : undefined)
    if (typeof chatSnapshot?.total_members === 'number' && chatSnapshot.total_members > 0) {
      return
    }

    const key = String(chatId)
    const existing = memberCountRefreshInFlight.get(key)
    if (existing) {
      await existing
      return
    }

    const run = (async () => {
      try {
        // pageSize: 1 — только ради count; без count не пишем members.length (это ≤1).
        const page = await ChatApiService.fetchMembers(chatId, { pageSize: 1 })
        const total = page.count
        if (typeof total !== 'number' || total < 0) {
          return
        }

        const patch = (chat: IChat): IChat => (
          chat.total_members === total ? chat : { ...chat, total_members: total }
        )

        let changed = false
        const idx = this.chats.findIndex(chat => sameChatId(chat.id, chatId))
        if (idx !== -1) {
          const next = patch(this.chats[idx])
          if (next !== this.chats[idx]) {
            this.chats[idx] = next
            changed = true
          }
        }

        if (this.currentChat && sameChatId(this.currentChat.id, chatId)) {
          const next = patch(this.currentChat)
          if (next !== this.currentChat) {
            this.currentChat = next
            changed = true
          }
        }

        if (changed) {
          syncViewWithCore()
        }
      } catch {
      }
    })()

    memberCountRefreshInFlight.set(key, run)
    try {
      await run
    } finally {
      memberCountRefreshInFlight.delete(key)
    }
  },

  // Создаёт личный диалог через backend POST /dialog/, затем подмешивает Matrix-активность
  async createDirectChat(
    employeeId: string,
    silent = false,
    hints?: { phone?: string; fullName?: string; avatar?: string | null; matrixUserId?: string },
  ): Promise<IChat> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    try {
      const employeeInfo = await this.resolveEmployeeInfo(employeeId)
      const fullName = hints?.fullName || employeeInfo.fullName
      const avatar = hints?.avatar ?? employeeInfo.avatar
      const peerMatrixUserId = hints?.matrixUserId || employeeInfo.matrixUserId

      let newChat = await ChatApiService.createOrGetDialog(employeeId)

      if (newChat.matrix_room_id) {
        try {
          const matrixChat = await fetchChatFromMatrix(newChat.id, newChat)
          newChat = mergeBackendChatWithMatrix(newChat, matrixChat)
        } catch {
        }
      } else {
        newChat = await pollChatUntilMatrixRoom(
          () => ChatApiService.fetchChatById(newChat.id),
          { attempts: 12, delayMs: 400 },
        ).catch(() => newChat)

        if (newChat.matrix_room_id) {
          try {
            const matrixChat = await fetchChatFromMatrix(newChat.id, newChat)
            newChat = mergeBackendChatWithMatrix(newChat, matrixChat)
          } catch {
            // Matrix ещё недоступен
          }
        }
      }

      if (peerMatrixUserId) {
        newChat = {
          ...newChat,
          matrix_peer_id: newChat.matrix_peer_id ?? peerMatrixUserId,
        }
      }

      if (fullName && isFallbackDialogTitle(newChat)) {
        newChat = { ...newChat, title: fullName }
      }

      if (!newChat.icon && avatar) {
        newChat = { ...newChat, icon: avatar }
      }

      const peerMxid = resolveDialogPeerMxid(newChat)
      if (peerMxid) {
        await trackMatrixPresence([peerMxid])
        const online = getCachedMatrixPresence(peerMxid) ?? false
        newChat = {
          ...newChat,
          online,
          last_seen_at: online ? null : getCachedMatrixLastSeenAt(peerMxid),
        }
      }

      this.upsertChatInList(newChat)

      if (!silent) {
        useFeedbackStore().showToast({
          type: 'success',
          message: 'Новый диалог создан',
          time: 3000,
        })
      }

      syncViewWithCore()
      this.syncActiveChatOnline()

      return newChat
    } catch (error) {
      logger.error('chat_createDirectChat_error', {
        file: 'chatStore',
        function: 'createDirectChat',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось создать диалог',
        time: 7000,
      })
      throw error
    }
  },

  async findOrCreateDirectChat(
    userId: string,
    silent = false,
    hints?: { phone?: string; fullName?: string; avatar?: string | null; matrixUserId?: string },
  ): Promise<IChat> {
    const currentUser = useCurrentUser()
    const currentUserId = currentUser.id.value

    if (!currentUserId) {
      throw new Error('Текущий пользователь не определен')
    }

    const existingDialog = await this.findExistingDirectChat(userId)

    if (existingDialog) {
      if (existingDialog.matrix_room_id && isForgottenRoom(existingDialog.matrix_room_id)) {
        return await this.createDirectChat(userId, silent, hints)
      }

      const peerMxid = resolveDialogPeerMxid(existingDialog)
      if (peerMxid) {
        await trackMatrixPresence([peerMxid])
        const online = getCachedMatrixPresence(peerMxid) ?? false
        const lastSeenAt = online ? null : getCachedMatrixLastSeenAt(peerMxid)
        if (existingDialog.online !== online || existingDialog.last_seen_at !== lastSeenAt) {
          existingDialog.online = online
          existingDialog.last_seen_at = lastSeenAt
          this.upsertChatInList(existingDialog)
          this.syncActiveChatOnline()
          syncViewWithCore()
        }
      }

      return existingDialog
    }

    return await this.createDirectChat(userId, silent, hints)
  },

  removeChatLocally(chatId: number): void {
    this.chats = this.chats.filter(chat => chat.id !== chatId)

    if (this.currentChat?.id === chatId) {
      dropRoomMessages(this, this.activeTopic?.matrix_room_id ?? this.currentChat.matrix_room_id)
      this.currentChat = null
      clearMessages(this)
    }

    notifyViewRemoveChat(chatId)
  },

  async handleMatrixPeerDeleteRequest(roomId: string): Promise<void> {
    const chat = this.chats.find(item => item.matrix_room_id === roomId)
    const chatId = chat?.id ?? roomIdToChatId(roomId)

    if (this.currentChat?.matrix_room_id === roomId) {
      clearMatrixSubscription('room')
    }

    try {
      await leaveAndForgetMatrixRoom(roomId)
      this.removeChatLocally(chatId)

      clearStoredSelectedChatId(chatId)

      this.updateTitleUnreadCount()
      syncViewWithCore()
    } catch (error) {
      logger.error('chat_handleMatrixPeerDeleteRequest_error', {
        file: 'chatStore',
        function: 'handleMatrixPeerDeleteRequest',
        condition: String(error),
      })
    }
  },

  async deleteChat(
    chatId: number,
    options?: { deleteForPeer?: boolean; destroy?: boolean },
  ): Promise<void> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    const chat = this.chats.find(item => item.id === chatId) || this.currentChat
    if (!chat || chat.id !== chatId) {
      throw new Error('Чат не найден')
    }

    try {
      const isDialog = chat.type === 'dialog' || chat.type === 'direct'
      const isBackendChat = !isSyntheticMatrixChatId(chatId, { matrix_room_id: chat.matrix_room_id })

      if (isBackendChat && !isDialog) {
        // destroy — полное удаление группы; иначе — выход участника
        if (options?.destroy) {
          await ChatApiService.deleteChat(chatId)
        } else {
          await ChatApiService.leaveChat(chatId)
        }
      } else if (chat.matrix_room_id) {
        if (options?.deleteForPeer && isDialog) {
          await deleteMatrixChat(chat, options)
        } else {
          await leaveAndForgetMatrixRoom(chat.matrix_room_id)
        }
      }

      this.removeChatLocally(chatId)
      this.updateTitleUnreadCount()
      syncViewWithCore()
    } catch (error) {
      logger.error('chat_deleteChat_error', {
        file: 'chatStore',
        function: 'deleteChat',
        condition: String(error),
      })
      throw error
    }
  },
})
